import PropTypes from 'prop-types';
import { action, reaction, extendObservable } from 'mobx';
import moment from 'moment';
import { apiCall } from '../../../API';
import { lenAwareEmailValidator } from '../../../email';
import { getGroupOptionDetails } from '../../helpers/userGroups';
import FuseSearchableSelectStore from '../../FuseSearchableSelectStore';
import InputStore from '../../InputStore';
import RadioButtonStore from '../../RadioButtonStore';
import UrlBulkEditorCardStore from '../../UrlBulkEditorCardStore';
import AsyncActionStore from '../../AsyncActionStore';
import FuseSelectStore from '../../FuseSelectStore';
import { PolicyModalSectionProps } from '../PolicyModalStore';
import { USER_GROUPS_SOURCES } from './BrowsingRolesStore';

export const USER_GROUP_TABS = {
    EMAILS: 'emails',
    GROUPS: 'groups'
};

const DEFAULT_VALUES = {
    effectsUserGroups: [],
    effectsEmails: []
};

export default class UserGroupsStore {
    static PropType = PropTypes.shape({
        activeTab: RadioButtonStore.PropType.isRequired,
        effectsUserGroups: FuseSelectStore.PropType.isRequired,
        effectsEmails: UrlBulkEditorCardStore.PropType,
        source: PropTypes.string.isRequired,
        editConfirmRemovedSearch: InputStore.PropType,
        editConfirmAddedSearch: InputStore.PropType,
        loadStatus: AsyncActionStore.PropType,
        loadLastSyncStatus: AsyncActionStore.PropType,
        referencesUnSyncedGroups: PropTypes.func,
        allGroupsHash: PropTypes.object,
        lastSyncGroup: PropTypes.number,
        getCustomerDomains: PropTypes.func,
        loadGroups: PropTypes.func,
        loadLastSyncGroup: PropTypes.func,
        ...PolicyModalSectionProps,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class UserGroupsStore */{
            activeTab: new RadioButtonStore(USER_GROUP_TABS.GROUPS),
            effectsUserGroups: new FuseSearchableSelectStore({
                sorterFactory: this.groupsSorterFactory,
                getSearchParams: (email) => ({ email }),
                apiMethod: apiCall('/console/usergroups/search'),
                searchValidator: lenAwareEmailValidator
            }),
            effectsEmails: new UrlBulkEditorCardStore({
                validators: [lenAwareEmailValidator, this.customerDomainValidator]
            }),
            loadStatus: new AsyncActionStore(),
            apiLoad: apiCall('/console/usergroups/list'),
            loadLastSyncStatus: new AsyncActionStore(),
            apiLoadLastSync: apiCall('/console/usergroups/cloudsync/updateTime?alwaysDisplay=false'),
            editConfirmAddedSearch: new InputStore(),
            editConfirmRemovedSearch: new InputStore(),
            source: USER_GROUPS_SOURCES.NONE, // A reaction in the browser role store keeps this up to date
            allGroupsHash: {},
            originalValue: {},
            lastSyncGroupDate: null,
            getCustomerDomains: () => [],
        }, data);

        reaction(() => this.getCustomerDomains, () => {
            if (this.getCustomerDomains().length > 0) {
                this.effectsEmails.urlBulkEditorStore.bulkEditorStore.updateUrlBulkInfo();
            }
        });
    }

    /** Customer domains are stored in the Console store. Rather than nesting stores, we let the component inject */
    setCustomerDomainsGetter = (getter) => {
        this.getCustomerDomains = getter;
    }

    isCustomerDomainValidator = (index) =>
        index === this.effectsEmails.getValidators().indexOf(this.customerDomainValidator)

    customerDomainValidator = (v) => {
        // Fails on the email validator, dont show extra error message
        if (!lenAwareEmailValidator(v)) {
            return true;
        }
        const candidateDomain = v.split('@')[1].toLowerCase();
        return this.getCustomerDomains().map((d) => d.toLowerCase()).includes(candidateDomain);
    }

    load = () => {
        this.loadGroups();
        this.loadLastSyncGroup();
    }

    hashByValue = (arr) =>
        arr.reduce((acc, item) => {
            acc[this.getGroupValue(item)] = item;
            return acc;
        }, {});

    loadGroups = () => {
        if (!this.loadStatus.isLoading) {
            this.loadStatus.setLoading(this.apiLoad()
                .then((result) => {
                    const { groups = [], lastSyncDate } = result || {};
                    this.allGroupsHash = this.hashByValue(groups);
                    // mark deleted user groups if selected not in all groups
                    this.effectsUserGroups.getSelectedItems().forEach((item) => {
                        item.hidden = this.isGroupUnSynced(item);
                    });
                    this.effectsUserGroups.setOptions(groups.map(this.groupToOption), true);
                    if (this.source === USER_GROUPS_SOURCES.SAML) {
                        this.updateLastSyncGroupDate(lastSyncDate);
                    }
                })
                .catch(() => {
                    this.allGroupsHash = {};
                    this.lastSyncGroupDate = '';
                }));
        }
        return this.loadStatus.promise;
    };

    loadLastSyncGroup = () => {
        if (!this.loadLastSyncStatus.isLoading) {
            this.loadLastSyncStatus.setLoading(this.apiLoadLastSync().then((lastSync) => {
                if (this.source === USER_GROUPS_SOURCES.TAP) {
                    this.updateLastSyncGroupDate(lastSync);
                }
            }));
        }
        return this.loadLastSyncStatus.promise;
    }

    updateLastSyncGroupDate = (lastSync) => {
        if (!lastSync) {
            this.lastSyncGroupDate = '';
        } else {
            const date = moment.utc(lastSync);
            const daysDiff = moment.utc().diff(date, 'days');
            this.lastSyncGroupDate = daysDiff <= 30 ?
                date.format('MMM/DD/YYYY [at] hh:mma [(GMT)]') : 'More than 30 Days ago';
        }
    }

    sortHash = (gr) =>
        `${gr.hidden ? 'A|' : 'Z|'}${gr.label}`;

    groupsSorterFactory = (options) => {
        const optsHash = this.hashByValue(options);
        return (v1, v2) => {
            const a = optsHash[v1];
            const b = optsHash[v2];
            return this.sortHash(a) > this.sortHash(b) ? 1 : -1;
        };
    }

    referencesUnSyncedGroups = () =>
        this.effectsUserGroups.getSelectedItems().some((gr) => gr.hidden);

    getGroupValue = (gr) => gr.id;

    getGroupLabel = (gr) => {
        const label = gr.name || gr.id;
        const suffix = this.isGroupUnSynced(gr) ? ' (group not synchronized)' : '';
        return label + suffix;
    };

    isGroupUnSynced = (gr) => {
        return !this.allGroupsHash[this.getGroupValue(gr)];
    }

    groupToOption = (gr) => {
        const groupOption = {
            label: gr.name || gr.id,
            value: this.getGroupValue(gr),
            hidden: this.isGroupUnSynced(gr),
            ...getGroupOptionDetails(gr),
            ...gr
        };
        if (this.isGroupUnSynced(gr)) {
            groupOption.label += ' (group not synchronized)';
            groupOption.icon = null;
        }
        return groupOption;
    }

    optionToGroup = ({ label, value, icon, info, hidden, ...groupProps }) => groupProps;

    setValue = action((value) => {
        value = { ...DEFAULT_VALUES, ...value };
        value.effectsUserGroups = value.effectsUserGroups || [];
        value.effectsEmails = value.effectsEmails || [];
        this.originalValue = value;
        this.isDefault = value.isDefault;
        this.effectsUserGroups.setOptions(value.effectsUserGroups.map(this.groupToOption));
        this.effectsUserGroups.setSelectedValues(value.effectsUserGroups.map(this.getGroupValue));
        this.effectsEmails.setValue(value.effectsEmails);
    });

    getValue = () => ({
        effectsUserGroups: this.effectsUserGroups.selectedValues.reduce((acc, v) => {
            acc.result.push(this.optionToGroup(acc.hash[v]));
            return acc;
        }, {
            hash: this.effectsUserGroups.options.reduce((acc, v) => {
                acc[v.value] = v;
                return acc;
            }, {}),
            result: []
        }).result,
        effectsEmails: this.effectsEmails.getValue(),
    });

    hasChanged = () =>
        this.originalValue.effectsEmails.length !== this.effectsEmails.getValue().length ||
        this.effectsEmails.getValue().some((item, index) => item !== this.originalValue.effectsEmails[index]) ||
        this.originalValue.effectsUserGroups.length !== this.effectsUserGroups.selectedValues.length ||
        this.effectsUserGroups.selectedValues.some((val, index) => {
            const originalGroup = this.originalValue.effectsUserGroups[index];
            return !originalGroup || val !== this.getGroupValue(originalGroup);
        });

    isValid = () => this.effectsEmails.isValid() && (
        this.originalValue.isDefault ||
        (this.effectsEmails.getValue().length > 0 || this.effectsUserGroups.selectedValues.length > 0)
    )
}
